import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Autocomplete from '@mui/joy/Autocomplete';
import CircularProgress from '@mui/joy/CircularProgress';
import Button from '@mui/joy/Button';
import { listTrackingEventTypes } from '../API/trackingEventType';
import { fetchAllPages } from '../utils/pagination';
import { formatTrackingEventTypeCatalogLabelWithCode } from '../utils/trackingEventLabels';
import { PERMISSIONS } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

export default function TrackingEventTypeOptionList({
  onChange,
  required,
  defaultValue,
  disabled = false,
  label,
  errorMessage,
}) {
  const { t, i18n } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const canListEventTypes = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST);
  const resolvedLabel = label ?? t('widgets.eventType');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Prefetch on mount (not only on open) so the first open already has
  // localized labels for the active UI language.
  useEffect(() => {
    if (!canListEventTypes) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const rows = await fetchAllPages(
          (page, pageSize) => listTrackingEventTypes({
            page,
            page_size: pageSize,
            ordering: 'sort_order',
          }),
        );
        const sorted = [...rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        if (!cancelled) setOptions(sorted);
      } catch (err) {
        console.error('TrackingEventTypeOptionList:', err);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canListEventTypes]);

  return (
    <Box
      sx={{
        marginBottom: 2,
        position: 'relative',
        '&:focus-within': {
          boxShadow: '0 0 8px rgba(0, 128, 255, 0.3)',
          borderRadius: '8px',
        },
      }}
    >
      <FormLabel
        sx={{
          fontWeight: 600,
          color: '#555',
          marginBottom: '6px',
          fontSize: '14px',
        }}
      >
        {required ? `${resolvedLabel} *` : resolvedLabel}
      </FormLabel>
      <Autocomplete
        key={`tracking-event-type-${i18n.language}`}
        value={defaultValue == null || defaultValue === '' ? null : defaultValue}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        placeholder={t('widgets.searchEventTypes')}
        options={Array.isArray(options) ? options : []}
        loading={loading}
        disabled={disabled || !canListEventTypes}
        onChange={onChange}
        isOptionEqualToValue={(option, value) => option?.code === value?.code}
        getOptionLabel={(option) => {
          if (!option || typeof option !== 'object') return '';
          // Prefer catalog row from loaded options so name_ar / code resolve correctly
          // even when the parent only passed { code, name: code }.
          const matched =
            option.code != null
              ? (Array.isArray(options) ? options : []).find((o) => o?.code === option.code)
              : null;
          return formatTrackingEventTypeCatalogLabelWithCode(matched || option, i18n.language);
        }}
        noOptionsText={
          !canListEventTypes ? (
            <span>
              {t('permissions.noPermission', {
                defaultValue: 'You do not have permission to perform this action.',
              })}
            </span>
          ) : (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            px={2}
          >
            <span>{t('widgets.typeToSearch')}</span>
            <Button
              variant="solid"
              color="primary"
              size="sm"
              onClick={() => window.open('/settings/tracking-event-types', '_blank')}
            >
              {t('widgets.manageEventTypes')}
            </Button>
          </Box>
          )
        }
        loadingText={t('actions.loading')}
        endDecorator={
          loading && (
            <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} />
          )
        }
        sx={{
          backgroundColor: '#f7f9fc',
          borderRadius: '8px',
          padding: '4px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            borderColor: '#5f09c2',
          },
          '&:focus': {
            borderColor: '#5f09c2',
            outline: 'none',
            boxShadow: '0 0 6px rgba(0, 123, 255, 0.5)',
          },
          '&[disabled]': {
            backgroundColor: '#e9ecef',
            color: '#6c757d',
            cursor: 'not-allowed',
          },
          ...(errorMessage
            ? {
                borderColor: 'var(--joy-palette-danger-500, #c41c1c)',
                boxShadow: '0 0 0 1px var(--joy-palette-danger-500, #c41c1c)',
              }
            : {}),
        }}
      />
      {errorMessage ? (
        <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>{errorMessage}</FormHelperText>
      ) : null}
    </Box>
  );
}
